import { format, subDays } from 'date-fns'
import { USE_OPS_MOCKS } from 'pages/Operations/Shared/opsControl'
import { formatGhs } from 'pages/FuelManagement/fuelOpsDemoData'
import { ISelectValue } from 'constants/globalTypes'
import { IStatusPill } from 'types/ui/statusPill.interface'
import {
  IInventoryAdjustmentRow,
  IInventoryChargeTo,
  IInventoryCountLine,
  IInventoryCountScheduleRow,
  IInventoryCountSheetMap,
  IInventoryIssueRow,
  IInventoryStockTake,
  IInventoryTransferControl,
  IInventoryTransferRow,
} from 'types/inventoryControl.interface'

const year = new Date().getFullYear()
const todayISO = format(new Date(), 'yyyy-MM-dd')
const yesterdayISO = format(subDays(new Date(), 1), 'yyyy-MM-dd')
const twoDaysAgoISO = format(subDays(new Date(), 2), 'yyyy-MM-dd')
const yesterdayLabel = format(subDays(new Date(), 1), 'd MMM')
const fixedISO = (monthDay: string, time: string): string => `${year}-${monthDay}T${time}:00`

export const signedGhs = (value: number): string =>
  value < 0 ? `−${formatGhs(Math.abs(value))}` : value > 0 ? `+${formatGhs(value)}` : formatGhs(0)

export const STOCK_TAKE_DANGER_PCT = 1

export const stockTakeVarianceTone = (
  varianceGhs: number | null,
  sheetValueGhs: number | null,
): 'danger' | 'normal' => {
  if (varianceGhs == null || varianceGhs >= 0 || !sheetValueGhs) return 'normal'
  return (Math.abs(varianceGhs) / sheetValueGhs) * 100 > STOCK_TAKE_DANGER_PCT ? 'danger' : 'normal'
}

export const countProgressLabel = (counted: number, total: number | null): string =>
  total == null ? '—' : `${counted} / ${total}`

export const lineVarianceQty = (line: IInventoryCountLine): number | null =>
  line.countedQty == null ? null : line.countedQty - line.systemQty

export const lineVarianceGhs = (line: IInventoryCountLine): number | null => {
  const qty = lineVarianceQty(line)
  return qty == null ? null : qty * line.unitCost
}

export const STOCK_TAKE_STATUS_META: {
  [k in IInventoryStockTake['status']]: { label: string; tone: IStatusPill['status'] }
} = {
  'SCHEDULED': { label: 'Scheduled', tone: 'neutral' },
  'IN_PROGRESS': { label: 'In progress', tone: 'info' },
  'AWAITING_APPROVAL': { label: 'Awaiting approval', tone: 'warning' },
  'COMPLETED': { label: 'Completed', tone: 'success' },
}

export const STOCK_TAKE_APPROVAL_BANNER = 'Adjustments posted to stock on approval'
export const STOCK_TAKE_VARIANCE_CAPTION =
  'Count variance feeds a stock adjustment with reason COUNT VARIANCE.'
export const STOCK_TAKE_APPROVER_ROLE = 'Inventory manager'
export const STOCK_TAKE_SUPERVISOR_ROLE = 'Supervisor'
export const STOCK_TAKE_DEVIATION_LIMIT_GHS = 1000

export const lineOverDeviationLimit = (
  line: IInventoryCountLine,
  limitGhs: number | null | undefined,
): boolean => {
  const value = lineVarianceGhs(line)
  return limitGhs != null && value != null && Math.abs(value) > limitGhs
}

export const deviationBannerText = (breachedLines: number, limitGhs: number): string =>
  `${breachedLines} ${breachedLines === 1 ? 'line exceeds' : 'lines exceed'} the ${formatGhs(limitGhs)} deviation limit — supervisor approval required`

export const STOCK_TAKE_BLIND_CAPTION =
  'Counters never see book quantities — prevents anchored counts.'
export const STOCK_TAKE_DEVIATION_CAPTION =
  'Variances over the limit need a supervisor — the storeman cannot approve their own big misses.'

export const STOCK_TAKE_LOCATION_OPTIONS: ISelectValue[] = [
  { value: 1, label: 'Main Warehouse' },
  { value: 2, label: 'Pit Store' },
  { value: 3, label: 'Workshop Store' },
]

const DEMO_STOCK_TAKES: IInventoryStockTake[] = [
  {
    id: 'ST-06',
    name: 'June cycle count — Main Warehouse',
    location: 'Main Warehouse',
    scheduledLabel: '28 Jun',
    startedLabel: '30 Jun',
    countedLines: 63,
    totalLines: 63,
    varianceGhs: -4110,
    sheetValueGhs: 312600,
    status: 'COMPLETED',
    approvedBy: STOCK_TAKE_APPROVER_ROLE,
    approvedAt: '1 Jul',
    deviationLimitGhs: STOCK_TAKE_DEVIATION_LIMIT_GHS,
  },
  {
    id: 'ST-07',
    name: 'Pit Store quarterly count',
    location: 'Pit Store',
    scheduledLabel: '3 Jul',
    startedLabel: '4 Jul',
    countedLines: 24,
    totalLines: 40,
    varianceGhs: -384,
    sheetValueGhs: 118400,
    status: 'IN_PROGRESS',
    approvedBy: null,
    approvedAt: null,
    blind: true,
    deviationLimitGhs: STOCK_TAKE_DEVIATION_LIMIT_GHS,
  },
  {
    id: 'ST-08',
    name: 'Workshop Store count',
    location: 'Workshop Store',
    scheduledLabel: '12 Jul',
    startedLabel: null,
    countedLines: 0,
    totalLines: null,
    varianceGhs: null,
    sheetValueGhs: null,
    status: 'SCHEDULED',
    approvedBy: null,
    approvedAt: null,
  },
]

const DEMO_COUNT_SHEETS: IInventoryCountSheetMap = {
  'ST-06': [
    {
      id: 1,
      sku: 'PPE-GLV1',
      item: 'Safety gloves (pair)',
      systemQty: 280,
      countedQty: 240,
      unitCost: 18,
      note: 'Feeds ADJ-119 — 40 pairs missing, security case SEC-77',
    },
    {
      id: 2,
      sku: 'OIL-HYD208',
      item: 'Hydraulic oil 208 L drum',
      systemQty: 13,
      countedQty: 12,
      unitCost: 3150,
      note: 'Feeds ADJ-119 — 1 drum short',
    },
    {
      id: 3,
      sku: 'WLD-0032',
      item: 'Welding electrodes 3.2 mm (box)',
      systemQty: 57,
      countedQty: 55,
      unitCost: 120,
      note: 'Feeds ADJ-119 — 2 boxes short',
    },
    { id: 4, sku: 'FLT-0917', item: 'Oil filter — CAT 777', systemQty: 47, countedQty: 47, unitCost: 85, note: null },
    { id: 5, sku: 'TYR-2749', item: 'Haul truck tyre 27.00R49', systemQty: 6, countedQty: 6, unitCost: 28400, note: null },
    { id: 6, sku: 'GET-3201', item: 'Bucket teeth — CAT 320', systemQty: 91, countedQty: 91, unitCost: 210, note: null },
    { id: 7, sku: 'DRL-0890', item: 'Drill rod 89 mm', systemQty: 14, countedQty: 14, unitCost: 1850, note: null },
  ],
  'ST-07': [
    {
      id: 11,
      sku: 'GET-3201',
      item: 'Bucket teeth — CAT 320',
      systemQty: 120,
      countedQty: 118,
      unitCost: 210,
      note: 'Two teeth on pit floor — recovered damaged',
    },
    {
      id: 12,
      sku: 'PPE-GLV1',
      item: 'Safety gloves (pair)',
      systemQty: 96,
      countedQty: 98,
      unitCost: 18,
      note: 'Two pairs returned uncounted last week',
    },
    { id: 13, sku: 'WLD-0032', item: 'Welding electrodes 3.2 mm (box)', systemQty: 40, countedQty: 40, unitCost: 120, note: null },
    { id: 14, sku: 'DRL-0890', item: 'Drill rod 89 mm', systemQty: 26, countedQty: null, unitCost: 1850, note: null },
    { id: 15, sku: 'OIL-HYD208', item: 'Hydraulic oil 208 L drum', systemQty: 9, countedQty: null, unitCost: 3150, note: null },
    { id: 16, sku: 'TYR-2749', item: 'Haul truck tyre 27.00R49', systemQty: 4, countedQty: null, unitCost: 28400, note: null },
  ],
}

export const INVENTORY_DEMO_STOCK_TAKES: IInventoryStockTake[] = USE_OPS_MOCKS ? DEMO_STOCK_TAKES : []
export const INVENTORY_DEMO_COUNT_SHEETS: IInventoryCountSheetMap = USE_OPS_MOCKS ? DEMO_COUNT_SHEETS : {}

export const STOCK_TAKE_SCHEDULE_CAPTION =
  'Counts are scheduled discipline, not annual panic — cadence per location.'
export const STOCK_TAKE_ABC_NOTE =
  'High-usage lines will count more often once ABC classes land (backend).'

export const STOCK_TAKE_CADENCE_OPTIONS: ISelectValue[] = [
  { value: 0, label: 'One-off' },
  { value: 1, label: 'Weekly' },
  { value: 2, label: 'Monthly' },
  { value: 3, label: 'Quarterly' },
]

const DEMO_COUNT_SCHEDULE: IInventoryCountScheduleRow[] = [
  {
    id: 1,
    location: 'Main Warehouse',
    cadence: 'Monthly — 1st',
    scope: 'Full count',
    lastDoneLabel: '30 Jun · ST-06',
    nextDueLabel: '1 Aug',
    state: 'OK',
    owner: 'A. Serwaa',
  },
  {
    id: 2,
    location: 'Pit Store',
    cadence: 'Quarterly',
    scope: 'Full count',
    lastDoneLabel: '4 Apr',
    nextDueLabel: '4 Oct',
    state: 'COUNTING',
    owner: 'K. Asante',
  },
  {
    id: 3,
    location: 'Workshop Store',
    cadence: 'Monthly — 12th',
    scope: 'Full count',
    lastDoneLabel: '12 Jun',
    nextDueLabel: '12 Jul · ST-08',
    state: 'OK',
    owner: 'K. Boateng',
  },
  {
    id: 4,
    location: 'Yard — consumables',
    cadence: 'Weekly spot-check',
    scope: 'High-value lines',
    lastDoneLabel: format(subDays(new Date(), 12), 'd MMM'),
    nextDueLabel: format(subDays(new Date(), 5), 'd MMM'),
    state: 'OVERDUE',
    owner: 'Y. Darko',
  },
]

export const INVENTORY_DEMO_COUNT_SCHEDULE: IInventoryCountScheduleRow[] = USE_OPS_MOCKS
  ? DEMO_COUNT_SCHEDULE
  : []

export const ISSUE_REASON_META: {
  [k in IInventoryAdjustmentRow['reason']]: { label: string; tone: IStatusPill['status'] }
} = {
  'DAMAGE': { label: 'Damage', tone: 'warning' },
  'THEFT': { label: 'Theft', tone: 'danger' },
  'COUNT_VARIANCE': { label: 'Count variance', tone: 'info' },
  'WASTE': { label: 'Waste', tone: 'neutral' },
  'EXPIRY': { label: 'Expiry', tone: 'neutral' },
}

export const ADJUSTMENT_REASON_META = ISSUE_REASON_META

export const CHARGE_KIND_META: { [k in IInventoryChargeTo['kind']]: string } = {
  'COST_CENTER': 'Cost center',
  'MACHINE': 'Machine',
  'WORK_ORDER': 'Work order',
}

export const ISSUE_REASON_KEYS: IInventoryAdjustmentRow['reason'][] = [
  'DAMAGE',
  'THEFT',
  'COUNT_VARIANCE',
  'WASTE',
  'EXPIRY',
]

export const ADJUSTMENT_REASON_KEYS = ISSUE_REASON_KEYS

export const CHARGE_KIND_KEYS: IInventoryChargeTo['kind'][] = ['COST_CENTER', 'MACHINE', 'WORK_ORDER']

export const ISSUE_REASON_OPTIONS: ISelectValue[] = ISSUE_REASON_KEYS.map((key, index) => ({
  value: index,
  label: ISSUE_REASON_META[key].label,
}))

export const ADJUSTMENT_REASON_OPTIONS = ISSUE_REASON_OPTIONS

export const CHARGE_KIND_OPTIONS: ISelectValue[] = CHARGE_KIND_KEYS.map((key, index) => ({
  value: index,
  label: CHARGE_KIND_META[key],
}))

export const chargeToDisplay = (chargeTo: IInventoryChargeTo): string =>
  `${CHARGE_KIND_META[chargeTo.kind]} — ${chargeTo.label}`

export const ISSUE_CONTROL_PENDING_CAPTION =
  'Charge-to stays on this screen for now — it is not sent to the backend yet.'

export const ISSUES_PAGE_SUBLINE =
  'Consumables issued to people, machines and cost centers — issued stock does not return. Returnable tools live under Assignments.'

export const ASSIGNMENTS_PAGE_SUBLINE =
  'Returnable items — assigned to a person, returned on unassign. Consumables are issued under Issues.'

export const ADJUSTMENTS_INTRO =
  'Stock corrections — damage, theft, waste, expiry and count variance. Write-offs need sign-off.'

export const issueLinesSummary = (lines: IInventoryIssueRow['lines']): string => {
  if (!lines?.length) return '—'
  if (lines.length === 1) {
    const line = lines[0]
    return `${line.inventoryItem ? line.inventoryItem.name : 'Item'} ×${line.quantity}`
  }
  const units = lines.reduce((sum, line) => sum + (line.quantity || 0), 0)
  return `${lines.length} lines · ${units} units`
}

export const ADJUSTMENT_STATUS_META: {
  [k in IInventoryAdjustmentRow['status']]: { label: string; tone: IStatusPill['status'] }
} = {
  'OPEN': { label: 'Open', tone: 'warning' },
  'POSTED': { label: 'Posted', tone: 'success' },
}

const demoUser = (firstName: string, lastName: string, id: number) => ({ id, firstName, lastName })

const demoItem = (id: number, name: string, sku: string) => ({
  id,
  name,
  sku,
  trackingMode: 0,
  lifecycleExpirationType: 0,
  lifecycleStartSourceType: 0,
})

const DEMO_ISSUES: IInventoryIssueRow[] = [
  {
    id: 122,
    companyId: 1,
    date: `${todayISO}T07:40:00`,
    performedByUser: demoUser('Ama', 'Serwaa', 43),
    assignedForUser: demoUser('HR onboarding', '— new hires', 47),
    notes: '',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    lines: [
      {
        id: 1221,
        quantity: 24,
        unitCost: 18,
        lineNotes: 'PPE starter packs for the July intake',
        inventoryItem: demoItem(9001, 'Safety gloves (pair)', 'PPE-GLV1'),
      },
    ],
    createdAt: `${todayISO}T07:40:00`,
    control: {
      chargeTo: { kind: 'COST_CENTER', label: 'HR' },
      valueGhs: 432,
    },
  },
  {
    id: 121,
    companyId: 1,
    date: `${yesterdayISO}T06:55:00`,
    performedByUser: demoUser('Kwame', 'Boateng', 41),
    assignedForUser: demoUser('Workshop', 'day crew', 46),
    notes: '',
    fromLocation: { id: 3, name: 'Workshop Store' },
    lines: [
      {
        id: 1211,
        quantity: 6,
        unitCost: 120,
        lineNotes: 'Shift float for the fabrication bay',
        inventoryItem: demoItem(32, 'Welding electrodes 3.2 mm (box)', 'WLD-0032'),
      },
    ],
    createdAt: `${yesterdayISO}T06:55:00`,
    control: {
      chargeTo: { kind: 'COST_CENTER', label: 'Maintenance' },
      valueGhs: 720,
    },
  },
  {
    id: 123,
    companyId: 1,
    date: `${yesterdayISO}T10:15:00`,
    performedByUser: demoUser('Kwame', 'Boateng', 41),
    assignedForUser: demoUser('D. Mensah', '(fitter)', 48),
    notes: '',
    fromLocation: { id: 3, name: 'Workshop Store' },
    lines: [
      {
        id: 1231,
        quantity: 4,
        unitCost: 85,
        lineNotes: '500-hour service — Truck 05',
        inventoryItem: demoItem(917, 'Oil filter — CAT 777', 'FLT-0917'),
      },
    ],
    createdAt: `${yesterdayISO}T10:15:00`,
    control: {
      chargeTo: { kind: 'MACHINE', label: 'Truck 05 · WO-4471' },
      valueGhs: 340,
    },
  },
  {
    id: 124,
    companyId: 1,
    date: `${twoDaysAgoISO}T13:05:00`,
    performedByUser: demoUser('Ama', 'Serwaa', 43),
    assignedForUser: demoUser('Plant', 'maintenance', 49),
    notes: '',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    lines: [
      {
        id: 1241,
        quantity: 1,
        unitCost: 3150,
        lineNotes: 'Crusher hydraulic pack top-up',
        inventoryItem: demoItem(208, 'Hydraulic oil 208 L drum', 'OIL-HYD208'),
      },
    ],
    createdAt: `${twoDaysAgoISO}T13:05:00`,
    control: {
      chargeTo: { kind: 'COST_CENTER', label: 'Processing' },
      valueGhs: 3150,
    },
  },
]

export const INVENTORY_DEMO_ISSUES: IInventoryIssueRow[] = USE_OPS_MOCKS ? DEMO_ISSUES : []

const DEMO_ADJUSTMENTS: IInventoryAdjustmentRow[] = [
  {
    id: 'ADJ-118',
    dateLabel: yesterdayLabel,
    reason: 'DAMAGE',
    itemsSummary: 'Oil filter — CAT 777 ×4',
    valueGhs: -340,
    chargeTo: { kind: 'MACHINE', label: 'Forklift WH-02' },
    status: 'POSTED',
    note: 'Oil filter crate dropped — 4 damaged',
  },
  {
    id: 'ADJ-120',
    dateLabel: '3 Jul',
    reason: 'THEFT',
    itemsSummary: 'Safety gloves (pair) ×40',
    valueGhs: -720,
    chargeTo: { kind: 'COST_CENTER', label: 'Security investigation' },
    status: 'OPEN',
    note: 'Safety gloves 40 pairs missing — security case SEC-77',
  },
  {
    id: 'ADJ-119',
    dateLabel: '1 Jul',
    reason: 'COUNT_VARIANCE',
    itemsSummary: 'Gloves −40 · Oil drum −1 · Electrode boxes −2',
    valueGhs: -4110,
    chargeTo: { kind: 'COST_CENTER', label: 'Warehouse ops' },
    status: 'POSTED',
    note: 'From ST-06 June count',
  },
]

export const INVENTORY_DEMO_ADJUSTMENTS: IInventoryAdjustmentRow[] = USE_OPS_MOCKS
  ? DEMO_ADJUSTMENTS
  : []

export const TRANSFER_STATUS_META: {
  [k in IInventoryTransferControl['status']]: { label: string; tone: IStatusPill['status'] }
} = {
  'DRAFT': { label: 'Draft', tone: 'neutral' },
  'DISPATCHED': { label: 'Dispatched', tone: 'info' },
  'IN_TRANSIT': { label: 'In transit', tone: 'warning' },
  'RECEIVED': { label: 'Received', tone: 'success' },
}

const DEMO_TRANSFERS: IInventoryTransferRow[] = [
  {
    id: 222,
    companyId: 1,
    date: `${todayISO}T06:50:00`,
    assignedForUser: demoUser('Yaw', 'Darko', 42),
    performedByUser: demoUser('Kwame', 'Boateng', 41),
    notes: 'Workshop service stock',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    toLocation: { id: 3, name: 'Workshop Store' },
    lines: [
      {
        id: 2221,
        quantity: 10,
        unitCost: 85,
        lineNotes: '',
        inventoryItem: demoItem(917, 'Oil filter — CAT 777', 'FLT-0917'),
      },
    ],
    createdAt: `${todayISO}T06:50:00`,
    control: {
      status: 'DISPATCHED',
      dispatchedAt: 'Today 06:50',
      receivedAt: null,
      receivedBy: null,
      varianceNote: null,
    },
  },
  {
    id: 221,
    companyId: 1,
    date: `${yesterdayISO}T09:40:00`,
    assignedForUser: demoUser('Kofi', 'Asante', 45),
    performedByUser: demoUser('Kwame', 'Boateng', 41),
    notes: 'Pit face drilling resupply',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    toLocation: { id: 2, name: 'Pit Store' },
    lines: [
      {
        id: 2211,
        quantity: 8,
        unitCost: 1850,
        lineNotes: '',
        inventoryItem: demoItem(890, 'Drill rod 89 mm', 'DRL-0890'),
      },
    ],
    createdAt: `${yesterdayISO}T09:40:00`,
    control: {
      status: 'IN_TRANSIT',
      dispatchedAt: 'Yesterday 09:40',
      receivedAt: null,
      receivedBy: null,
      varianceNote: null,
    },
  },
  {
    id: 219,
    companyId: 1,
    date: fixedISO('06-29', '14:05'),
    assignedForUser: demoUser('Kofi', 'Asante', 45),
    performedByUser: demoUser('Ama', 'Serwaa', 43),
    notes: 'Pit PPE replenishment',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    toLocation: { id: 2, name: 'Pit Store' },
    lines: [
      {
        id: 2191,
        quantity: 60,
        unitCost: 18,
        lineNotes: '',
        inventoryItem: demoItem(9001, 'Safety gloves (pair)', 'PPE-GLV1'),
      },
    ],
    createdAt: fixedISO('06-29', '14:05'),
    control: {
      status: 'RECEIVED',
      dispatchedAt: '29 Jun 14:05',
      receivedAt: '30 Jun 10:12',
      receivedBy: 'K. Storeman',
      varianceNote: null,
    },
  },
  {
    id: 215,
    companyId: 1,
    date: fixedISO('06-23', '08:30'),
    assignedForUser: demoUser('Yaw', 'Darko', 42),
    performedByUser: demoUser('Kwame', 'Boateng', 41),
    notes: 'Workshop filter stock',
    fromLocation: { id: 1, name: 'Main Warehouse' },
    toLocation: { id: 3, name: 'Workshop Store' },
    lines: [
      {
        id: 2151,
        quantity: 12,
        unitCost: 85,
        lineNotes: '',
        inventoryItem: demoItem(917, 'Oil filter — CAT 777', 'FLT-0917'),
      },
    ],
    createdAt: fixedISO('06-23', '08:30'),
    control: {
      status: 'RECEIVED',
      dispatchedAt: '23 Jun 08:30',
      receivedAt: '24 Jun 11:45',
      receivedBy: 'E. Adjei',
      varianceNote: '1 filter short — stock adjustment raised',
    },
  },
]

export const INVENTORY_DEMO_TRANSFERS: IInventoryTransferRow[] = USE_OPS_MOCKS ? DEMO_TRANSFERS : []

export const TRANSFER_RECEIPT_OK_NOTE = 'Counted OK — no variance'
